import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { AppointmentService } from '../../core/services/appointment.service';
import { TimeSlot } from '../../core/models/appointment.model';
import { User } from '../../core/models/user.model';
import { SERVICE_TYPES, BOOKABLE_PROFESSIONALS } from '../../core/mock/mock-data';

@Component({
  selector: 'app-booking',
  standalone: false,
  templateUrl: './booking.component.html',
  styleUrl: './booking.component.scss'
})
export class BookingComponent implements OnInit, OnDestroy {
  professionals = BOOKABLE_PROFESSIONALS;
  selectedProfessional: User = BOOKABLE_PROFESSIONALS[0];

  availableDates: string[] = [];
  selectedDate = '';
  timeSlots: TimeSlot[] = [];
  selectedTime = '';
  serviceTypes = SERVICE_TYPES;
  selectedService = SERVICE_TYPES[0];
  notes = '';
  isLoading = false;
  error = '';

  /** Payment step state */
  showPayment = false;
  paymentProcessing = false;

  /** Id del turno original cuando estamos reprogramando */
  reschedulingId: string | null = null;

  private sub = new Subscription();

  constructor(
    private auth: AuthService,
    private apptSvc: AppointmentService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.availableDates = this.apptSvc.getAvailableDates(30);
    if (this.availableDates.length) {
      this.selectDate(this.availableDates[0]);
    }

    // Modo reprogramación: pre-cargar datos del turno original
    this.sub.add(
      this.route.queryParams.subscribe(params => {
        const id = params['reschedule'];
        if (!id) return;
        const original = this.apptSvc.getById(id);
        if (!original) return;
        this.reschedulingId = id;
        const svc = this.serviceTypes.find(s => s.label === original.serviceType);
        if (svc) this.selectedService = svc;
        this.notes = original.notes ?? '';
      })
    );
  }

  selectDate(date: string): void {
    this.selectedDate = date;
    this.selectedTime = '';
    this.timeSlots = this.apptSvc.slotsForDate(this.selectedProfessional.id, date);
  }

  selectTime(slot: TimeSlot): void {
    if (!slot.available) return;
    this.selectedTime = slot.time;
  }

  selectService(svc: typeof SERVICE_TYPES[0]): void {
    this.selectedService = svc;
  }

  selectProfessional(pro: User): void {
    this.selectedProfessional = pro;
    this.selectedDate = '';
    this.selectedTime = '';
    if (this.availableDates.length) this.selectDate(this.availableDates[0]);
  }

  formatDate(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' });
  }

  formatDateFull(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }

  /** Touched state for form validation hints */
  submitted = false;

  get canSubmit(): boolean {
    return !!this.selectedDate && !!this.selectedTime && !this.isLoading;
  }

  get dateError(): boolean   { return this.submitted && !this.selectedDate; }
  get timeError(): boolean   { return this.submitted && !!this.selectedDate && !this.selectedTime; }

  /** Step 1: show the MercadoPago mock checkout panel */
  goToPayment(): void {
    this.submitted = true;
    if (!this.canSubmit) return;
    const user = this.auth.currentUserSnapshot;
    if (!user) { this.router.navigate(['/login']); return; }
    this.showPayment = true;
  }

  /** Step 2: simulate payment and create appointment */
  confirmPayment(): void {
    if (this.paymentProcessing) return;
    this.paymentProcessing = true;
    this.error = '';

    setTimeout(() => {
      // Si es reprogramación, cancelar el turno original
      if (this.reschedulingId) {
        this.apptSvc.cancel(this.reschedulingId);
      }

      const user = this.auth.currentUserSnapshot!;
      const apt = this.apptSvc.create({
        clientId: user.id,
        clientName: user.name,
        clientEmail: user.email,
        professionalId: this.selectedProfessional.id,
        professionalName: this.selectedProfessional.name,
        date: this.selectedDate,
        time: this.selectedTime,
        duration: this.selectedService.duration,
        serviceType: this.selectedService.label,
        price: this.selectedService.price,
        paymentStatus: 'paid',
        notes: this.notes,
      });
      this.paymentProcessing = false;
      this.router.navigate(['/confirm-booking'], { queryParams: { id: apt.id } });
    }, 1800);
  }

  cancelPayment(): void {
    this.showPayment = false;
  }

  submit(): void {
    this.goToPayment();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
